'use client'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import useSWRImmutable from 'swr/immutable'

export default function FrontEndBreadcrumbs() {
  const params = useParams()

  const activeItem =
    params.eventId || params.championshipSlug || params.communitySlug

  const { data: community, isLoading: communityLoading } = useSWRImmutable(
    params.communitySlug
      ? {
          url: `community/${params.communitySlug}`,
          searchParams: {
            select: {
              id: true,
              name: true,
            },
          },
        }
      : null,
    apiFetch,
  )

  const { data: championship, isLoading: championshipLoading } =
    useSWRImmutable(
      params.championshipSlug
        ? {
            url: `community/${params.communitySlug}/championship/${params.championshipSlug}`,
            searchParams: {
              select: {
                id: true,
                name: true,
                isSingleEvent: true,
              },
            },
          }
        : null,
      apiFetch,
    )

  const { data: event, isLoading: eventLoading } = useSWRImmutable(
    params.eventId
      ? {
          url: `community/${params.communitySlug}/championship/${params.championshipSlug}/event/${params.eventId}`,
          searchParams: {
            select: {
              id: true,
              name: true,
            },
          },
        }
      : null,
    apiFetch,
  )

  return (
    <div className="col-span-full flex items-center gap-2 text-p-1 desktop-xs:gap-4">
      {community ? (
        <div
          className={cn('max-w-1/2 flex items-center gap-2 desktop-xs:gap-4', {
            'max-w-1/2!': activeItem === params.communitySlug,
            'max-w-1/3!': activeItem === params.eventId,
          })}
        >
          <ChevronLeft className="size-4 min-w-4 stroke-neutral-blue-400 stroke-[2.5px]" />

          <Link href={`/c/${params.communitySlug}`}>
            <p
              className={cn(
                'truncate-1 break-all text-p-1 font-bold text-neutral-blue-400 transition-all ease-in-out hover:text-neutral-blue-200 desktop-xs:font-semibold',
                {
                  'text-shades-0 hover:text-shades-0/80':
                    activeItem === params.communitySlug,
                },
              )}
            >
              {community.name}
            </p>
          </Link>
        </div>
      ) : communityLoading ? (
        <Skeleton
          className={cn('max-w-1/2 h-[20px] w-[180px]', {
            'max-w-1/2!': activeItem === params.communitySlug,
            'max-w-1/3!': activeItem === params.eventId,
          })}
        />
      ) : null}

      {championship && !championship.isSingleEvent ? (
        <div
          className={cn('max-w-1/2 flex items-center gap-2 desktop-xs:gap-4', {
            'max-w-1/2!': activeItem === params.communitySlug,
            'max-w-1/3!': activeItem === params.eventId,
          })}
        >
          <ChevronRight className="size-4 min-w-4 stroke-neutral-blue-400 stroke-[2.5px]" />

          <Link href={`/c/${params.communitySlug}/${params.championshipSlug}`}>
            <p
              className={cn(
                'truncate-1 break-all text-p-1 font-bold text-neutral-blue-400 transition-all ease-in-out hover:text-neutral-blue-200 desktop-xs:font-semibold',
                {
                  'text-shades-0 hover:text-shades-0/80':
                    activeItem === params.championshipSlug,
                },
              )}
            >
              {championship.name}
            </p>
          </Link>
        </div>
      ) : championshipLoading ? (
        <Skeleton
          className={cn('max-w-1/2 h-[20px] w-[180px]', {
            'max-w-1/2!': activeItem === params.communitySlug,
            'max-w-1/3!': activeItem === params.eventId,
          })}
        />
      ) : null}

      {event ? (
        <div
          className={cn('max-w-1/2 flex items-center gap-2 desktop-xs:gap-4', {
            'max-w-1/2!': activeItem === params.communitySlug,
            'max-w-1/3!': activeItem === params.eventId,
          })}
        >
          <ChevronRight className="size-4 min-w-4 stroke-neutral-blue-400 stroke-[2px]" />

          <Link
            href={`/c/${params.communitySlug}/${params.championshipSlug}/${params.eventId}`}
          >
            <p
              className={cn(
                'truncate-1 break-all text-p-1 font-bold text-neutral-blue-400 transition-all ease-in-out hover:text-neutral-blue-200 desktop-xs:font-semibold',
                {
                  'text-shades-0 hover:text-shades-0/80':
                    activeItem === params.eventId,
                },
              )}
            >
              {event.name}
            </p>
          </Link>
        </div>
      ) : eventLoading ? (
        <Skeleton
          className={cn('max-w-1/2 h-[20px] w-[180px]', {
            'max-w-1/2!': activeItem === params.communitySlug,
            'max-w-1/3!': activeItem === params.eventId,
          })}
        />
      ) : null}
    </div>
  )
}
